import { Box, Stack } from "@mui/system";
import BudgetControlGraphItemCard from "./BudgetControlGraphItemCard";

// ─── Formatters (mirror Budget Control Desktop) ──────────────────────────────
const cleanNumber = (value: any): number =>
  parseFloat(String(value ?? "0").replace(/[% ,]/g, "")) || 0;

// Money: rounded, 1000-separated, negative as -$100, positive as $100
const formatMoney = (value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const num = Number(String(value).replace(/[, ]/g, ""));
  if (isNaN(num)) return "";
  const formatted = Math.abs(num).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
  return num < 0 ? `-$${formatted}` : `$${formatted}`;
};

// Percentage with up to 2 decimals (e.g. 95.5%) — used for the QFTD / RR badges
const formatPerc = (value: any): string =>
  value !== undefined && value !== null && value !== ""
    ? `${parseFloat(Number(value).toFixed(2))}%`
    : "";

// Whole-number percentage (e.g. 105%) — used for the budget-used summary
const formatPercRounded = (value: any): string =>
  value !== undefined && value !== null && value !== ""
    ? `${Math.round(Number(value))}%`
    : "";

//rounded down to the nearest integer(https://optival.atlassian.net/browse/GR-21152)
// QFTD budget: rounded down to nearest integer; empty when missing
const roundDownBudget = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isFinite(num)
    ? `$${Math.floor(num).toLocaleString("en-US")}`
    : "";
};

// Truncate toward zero (down for positive, up for negative); (https://optival.atlassian.net/browse/GR-21152)
// Remaining QFTD: truncated toward zero (down if positive, up if negative); empty when missing
const truncTowardZero = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(String(value).replace(/[, ]/g, ""));
  return Number.isFinite(num) ? String(Math.trunc(num)) : "";
};
// ─────────────────────────────────────────────────────────────────────────────

const BudgetControlGraphItem = ({ selectedRow }: { selectedRow?: any }) => {
  // Detail card is shown only for the bar the user selects (none by default)
  if (!selectedRow) return null;

  const qftdPerc = cleanNumber(selectedRow["perc_of_qftd_budget_used"]) * 100;
  const rrBudgetPerc =
    cleanNumber(selectedRow["perc_of_qftd_RR_budget_use"]) * 100;

  // QFTD Budget: floored raw value;
  const qftdBudgetValue = roundDownBudget(selectedRow["qftd_budget"]);
  const qftdBudgetStatus =
    qftdBudgetValue !== "" && Number(qftdBudgetValue) >= 100
      ? "error"
      : "success";

  return (
    <Box>
      <Stack spacing={2}>
        <BudgetControlGraphItemCard
          budget_percentage={formatPercRounded(
            cleanNumber(selectedRow["perc_of_budget_used"]) * 100,
          )}
          budget_dollar={
            selectedRow["qftd_budget"]
              ? roundDownBudget(selectedRow["qftd_budget"])
              : ""
          }
          budget_status={qftdBudgetStatus}
          qftd={String(selectedRow["qftd"] ?? "")}
          qftd_status={qftdPerc >= 100 ? "error" : "success"}
          qftd_percentage={
            selectedRow["perc_of_qftd_budget_used"] ? formatPerc(qftdPerc) : ""
          }
          rr_budget_status={rrBudgetPerc >= 100 ? "error" : "success"}
          rr_budget_percentage={
            selectedRow["perc_of_qftd_RR_budget_use"]
              ? formatPerc(rrBudgetPerc)
              : ""
          }
          remaining_qftd={
            selectedRow["remaining_qftd"] != null
              ? truncTowardZero(selectedRow["remaining_qftd"])
              : ""
          }
          cpa_income_dollar={formatMoney(selectedRow["cpa_income"])}
          total_income_dollar={formatMoney(selectedRow["total_income"])}
          cpa_budget_dollar={formatMoney(selectedRow["cpa_constraint"])}
          ftd={String(selectedRow["ftd"] ?? "")}
        />
      </Stack>
    </Box>
  );
};

export default BudgetControlGraphItem;
